import React, { useState, useEffect, useMemo } from 'react';
import axiosInstance from '../../../axiosInstance';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonModal from '../../common/CommonModal';
import useUnsavedChangesWarning from '../../../hooks/ui/navigation/useUnsavedChangesWarning';
import Stepper from 'apollo-react/components/Stepper';
import Step from 'apollo-react/components/Step';
import StepLabel from 'apollo-react/components/StepLabel';
import LinearProgress from 'apollo-react/components/LinearProgress';
import Paper from 'apollo-react/components/Paper';

import { Formik, Form, Field } from 'formik';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import ButtonGroup from 'apollo-react/components/ButtonGroup';
import Grid from 'apollo-react/components/Grid';
import { renderInputComponent } from '../../common/Form/renderInputComponent';
import DepartmentForm from '../../common/Org Tree/form';
import {
  fetchUsersAPI,
  createDepartmentPayload,
} from '../../common/Org Tree/utils/treeUtils';
import { validateForm } from '../../common/Org Tree/utils/validationUtils';
import { checkOrganizationNameExists } from '../../../services/organizationService';
import { organizationFormSchema } from '../../../validation/organization';
import styles from './form.module.css';

const steps = ['Step 1', 'Step 2'];

const OrganizationForm = () => {
  const [formFields, setFormFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [formDataCopy, setFormDataCopy] = useState({});
  const [categories, setCategories] = useState([]);
  const [isUpdate, setIsUpdate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [resp, setResp] = useState('');
  const [initialValues, setInitialValues] = useState({});
  const [discardModal, setDiscardModal] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  // Department form states
  const [users, setUsers] = useState([]);
  const [globalSelectedManagers, setGlobalSelectedManagers] = useState([]);
  const [globalSelectedEmployees, setGlobalSelectedEmployees] = useState([]);
  const [globalSelectedHeads, setGlobalSelectedHeads] = useState([]);
  const [currentNode, setCurrentNode] = useState(null);
  const [departmentFormData, setDepartmentFormData] = useState({});
  const [departmentFormErrors, setDepartmentFormErrors] = useState({});

  const [t] = useTranslation();
  const { id } = useParams();

  const isDirty = useMemo(() => {
    const keys = new Set([
      ...Object.keys(formData),
      ...Object.keys(formDataCopy),
    ]);
    for (const key of keys) {
      if (String(formData[key] ?? '') !== String(formDataCopy[key] ?? ''))
        return true;
    }
    if (!isUpdate && Object.keys(departmentFormData).length > 0) {
      const d = departmentFormData;
      if (
        (d.name && d.name.trim() !== '') ||
        (d.description && d.description.trim() !== '') ||
        (d.manager && d.manager.length > 0) ||
        (d.head && d.head.length > 0) ||
        (d.employees && d.employees.length > 0)
      )
        return true;
    }
    return false;
  }, [formData, formDataCopy, isUpdate, departmentFormData]);

  const { navigateBack } = useUnsavedChangesWarning(isDirty);

  // Fetch form fields and data (if updating) from the backend
  useEffect(() => {
    const fetchFormMetadata = async () => {
      try {
        // fetch categories
        const { data: categoriesResponse } = await axiosInstance.get(
          '/api/categories',
          {
            withCredentials: true,
            params: {
              status: 'published_active',
            },
          }
        );
        // Handle response format: { data: [...], total: number } or direct array
        const categoriesData = Array.isArray(categoriesResponse)
          ? categoriesResponse
          : categoriesResponse?.data || [];
        setCategories(Array.isArray(categoriesData) ? categoriesData : []);

        if (id) {
          // If we have an ID, fetch data for updating
          setIsUpdate(true);

          const { data: response } = await axiosInstance
            .get(`/api/v1/organizations/${id}`, {
              withCredentials: true,
            })
            .catch((err) => console.log(err));

          const organizationData = response.data;

          // set category from nested object if exists
          if (organizationData.category) {
            organizationData.category_id = organizationData.category.id;
          }

          setFormData(organizationData);
          setFormDataCopy(organizationData);
        }

        setLoading(false);
      } catch (err) {
        setResp(t('Error fetching form metadata or data'));
        console.error(t('Error fetching form metadata or data: '), err);
        setLoading(false);
      }
    };

    fetchFormMetadata();
  }, [id, t]);

  // Update form fields when isUpdate changes
  useEffect(() => {
    // Define organization form fields
    const organizationFields = [
      {
        name: 'name',
        type: 'text',
        label: t('organization_name') || 'Org Name',
        required: true,
      },
      {
        name: 'type',
        type: 'dropdown',
        label: t('organization_type') || 'Org Type',
        required: true,
        disabled: isUpdate, // Disable type field during updates
        options: [
          { label: t('internal') || 'Internal', key: 'internal' },
          { label: t('external') || 'External', key: 'external' },
        ],
      },
    ];

    // add category field only if type is external
    if (formData.type === 'external') {
      organizationFields.push({
        name: 'category_id',
        type: 'dropdown',
        label: t('category'),
        required: true,
        disabled: isUpdate, // disable category field during updates
        options: Array.isArray(categories)
          ? categories.map((cat) => ({
              key: cat.id,
              label: cat.name,
            }))
          : [],
      });
    }

    setFormFields(organizationFields);
  }, [isUpdate, t, formData.type, categories]);

  useEffect(() => {
    const initialValues = formFields.reduce((acc, field) => {
      acc[field.name] = formData[field.name] || '';
      return acc;
    }, {});

    setInitialValues(initialValues);
  }, [formFields, formData]);

  const fetchUsers = async () => {
    try {
      // fetch users based on organization type
      const organizationType = formData.type || 'internal';

      // for external orgs, use category-based filtering
      if (organizationType === 'external' && formData.category_id) {
        const activeUsers = await fetchUsersAPI(
          organizationType,
          null, // no organizationId for new orgs
          formData.category_id // pass category for filtering
        );
        setUsers(activeUsers);
      } else {
        const activeUsers = await fetchUsersAPI(organizationType);
        setUsers(activeUsers);
      }

      // Initialize a sample node for step 2
      if (!currentNode) {
        setCurrentNode({
          id: 'org-dept-1',
          level: 1,
          name: '',
          description: '',
          manager: [],
          head: [],
          employees: [],
          parent_id: null,
        });
      }
    } catch (error) {
      // error handling is done in the utility function
    }
  };

  useEffect(() => {
    if (Object.keys(departmentFormData).length > 0)
      setCurrentNode({ ...currentNode, ...departmentFormData });
  }, [departmentFormData]);

  // Fetch users for department form
  useEffect(() => {
    if (activeStep === 1) {
      fetchUsers();
    }
  }, [activeStep]);

  const handleBackStep = () => {
    setActiveStep(activeStep - 1);
  };

  const handleNextStep = () => {
    if (activeStep === steps.length - 1) {
      handleSubmit();
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  const handleChange = (name, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const validateOrganizationFormikFields = () => {
    const errors = {};

    if (!formData.name?.trim()) {
      errors.name =
        t('organization_name_required') || 'Organization name is required!';
    } else if (formData.name.trim().length < 3) {
      errors.name =
        t('organization_name_min_length') ||
        'Organization name must be at least 3 characters long!';
    } else if (formData.name.trim().length > 100) {
      errors.name =
        t('organization_name_max_length') ||
        'Organization name must not exceed 100 characters!';
    } else if (!/^[a-zA-Z0-9\s\-']+$/.test(formData.name.trim())) {
      errors.name =
        t('organization_name_invalid_chars') ||
        'Organization name can only contain alphanumeric characters, spaces, hyphens, and apostrophes!';
    }

    if (!formData.type) {
      errors.type =
        t('organization_type_required') || 'Organization type is required!';
    }

    if (formData.type === 'external' && !formData.category_id) {
      errors.category_id = t('category_required_for_external');
    }

    return errors;
  };

  const handleSubmit = async (values, formikHelpers) => {
    try {
      if (isUpdate) {
        await axiosInstance.put(`/api/v1/organizations/${id}`, formData);
        toast.success(
          t('organization_updated_successfully') ||
            'Organization Updated Successfully!',
          { position: 'top-center' }
        );
        navigateBack();
      } else {
        if (activeStep !== steps.length - 1) {
          const nameExists = await checkOrganizationNameExists(formData.name);
          if (nameExists) {
            formikHelpers?.setFieldError(
              'name',
              t('organization_name_already_exists') ||
                'Organization with this name already exists'
            );
            return;
          }
          handleNextStep();
          return false;
        }
        const isDepartmentValid = validateForm(
          departmentFormData,
          currentNode,
          t
        );
        if (!isDepartmentValid.isValid) {
          setDepartmentFormErrors(isDepartmentValid.errors);
          const firstError = Object.values(isDepartmentValid.errors).find(
            (e) => e
          );
          if (firstError) toast.error(firstError, { position: 'top-center' });
          return false;
        }
        const departmentPayload = createDepartmentPayload(currentNode);

        const { category_id, ...orgDataWithoutCategory } = formData;
        const organizationPayload =
          formData.type === 'internal' ? orgDataWithoutCategory : formData;

        const requestData = {
          organizationData: organizationPayload,
          departmentData: departmentPayload,
        };
        await axiosInstance.post('/api/v1/organizations/', requestData);
        toast.success(
          t('organization_created_successfully') ||
            'Organization Created Successfully!',
          { position: 'top-center' }
        );
        navigateBack();
      }
    } catch (err) {
      console.error('Error submitting form: ', err);
      const errorMsg =
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        t('Error submitting form');
      toast.error(errorMsg, { position: 'top-center' });
      return false;
    }
  };

  if (loading) return <div>Loading...</div>;

  const renderButtons = () => {
    const buttons = [];

    if (!isUpdate) {
      buttons.push({
        variant: 'secondary',
        label: t('Previous'),
        onClick: handleBackStep,
        disabled: activeStep === 0,
        className: styles.wizardButton,
      });
    }

    buttons.push({
      variant: 'primary',
      label: t('Discard'),
      status: 'danger',
      onClick: () => setDiscardModal(true),
      className: styles.wizardButton,
    });

    buttons.push({
      variant: 'primary',
      label: isUpdate
        ? t('Save')
        : activeStep === steps.length - 1
          ? t('Finish')
          : t('Next'),
      onClick: isUpdate || activeStep === 0 ? undefined : handleNextStep,
      type: isUpdate || activeStep === 0 ? 'submit' : 'button',
      className: styles.wizardButton,
    });

    return (
      <ButtonGroup
        alignItems="right"
        className={styles.buttonGroup}
        buttonProps={buttons}
      />
    );
  };

  const renderOrganizationForm = () => {
    return (
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        onSubmit={handleSubmit}
        validationSchema={toFormikValidationSchema(organizationFormSchema(t))}
      >
        {({ setFieldValue }) => (
          <Form>
            <Paper className={styles.paperContainer}>
              {formFields.map((field) => (
                <Field key={field.name} name={field.name}>
                  {({ field: formikField, meta }) => {
                    const handleFieldChange = (value) => {
                      console.log('formikField', formikField, value);
                      setFieldValue(formikField.name, value);
                      handleChange(formikField.name, value);
                      if (formikField.name === 'type' && value === 'internal') {
                        setFieldValue('category_id', '');
                        handleChange('category_id', '');
                      }
                    };
                    return renderInputComponent(
                      field,
                      formData,
                      formikField,
                      meta,
                      handleFieldChange
                    );
                  }}
                </Field>
              ))}
            </Paper>
            {renderButtons()}
          </Form>
        )}
      </Formik>
    );
  };

  const renderOrganizationWithDepartment = () => {
    return (
      <>
        <Grid size={{ xs: 12, md: 3 }}>
          <Stepper box activeStep={activeStep}>
            {steps.map((label) => (
              <Step key={label} className={styles.stepperContainer}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Grid>

        <Grid size={{ xs: 12, md: 9 }}>
          <div className={styles.progressContainer}>
            <LinearProgress
              variant="determinate"
              className={'progress-bar-workflow'}
              value={activeStep !== steps.length - 1 ? 50 : 100}
            />
          </div>

          <div>
            {activeStep !== steps.length - 1 ? (
              renderOrganizationForm()
            ) : (
              <div>
                {currentNode && (
                  <div>
                    <DepartmentForm
                      node={currentNode}
                      // editNode={handleEditNode}
                      // onCancel={handleCancelDepartmentEdit}
                      globalSelectedManagers={globalSelectedManagers}
                      setGlobalSelectedManagers={setGlobalSelectedManagers}
                      globalSelectedEmployees={globalSelectedEmployees}
                      setGlobalSelectedEmployees={setGlobalSelectedEmployees}
                      globalSelectedHeads={globalSelectedHeads}
                      setGlobalSelectedHeads={setGlobalSelectedHeads}
                      users={users}
                      isOpenedFromOrganizationForm={true}
                      data={departmentFormData}
                      setData={setDepartmentFormData}
                      errors={departmentFormErrors}
                      setErrors={setDepartmentFormErrors}
                    />
                    {renderButtons()}
                  </div>
                )}
              </div>
            )}
          </div>
        </Grid>
      </>
    );
  };

  // Enhanced form with stepper for create mode
  return (
    <Grid container spacing={2} sx={{ width: '100%' }}>
      {resp && <p>{resp}</p>}

      {isUpdate ? (
        <Grid size={12}>{renderOrganizationForm()}</Grid>
      ) : (
        renderOrganizationWithDepartment()
      )}

      <CommonModal
        open={discardModal}
        message="Are you sure you want to discard your changes?"
        buttonProps={[
          {
            label: 'Cancel',
            onClick: () => {
              setDiscardModal(false);
            },
          },
          {
            label: 'Confirm',
            onClick: () => {
              if (isUpdate) setFormData(formDataCopy);
              else {
                if (activeStep === steps.length - 1) {
                  setDepartmentFormData({
                    name: '',
                    description: '',
                    manager: [],
                    head: [],
                    employees: [],
                    parent_id: null,
                  });
                  setDepartmentFormErrors({});
                  setGlobalSelectedManagers([]);
                  setGlobalSelectedEmployees([]);
                  setGlobalSelectedHeads([]);
                } else setFormData(formDataCopy);
              }
              setDiscardModal(false);
            },
          },
        ]}
        hideHeader
        id="hideHeader"
      />
    </Grid>
  );
};

export default OrganizationForm;
